import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tvShows, categories } from '@/mocks/tvShows';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ShowCard from '@/components/base/ShowCard';

const categoryNameMap: Record<string, string> = {
  action: '动作冒险',
  suspense: '悬疑惊悚',
  scifi: '科幻奇幻',
  drama: '剧情爱情',
  crime: '犯罪刑侦',
  comedy: '喜剧',
  horror: '恐怖',
};

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const show = tvShows.find((s) => s.id === Number(id));
  const [activeSeason, setActiveSeason] = useState(0);

  if (!show) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-300" />
            </div>
            <p className="text-foreground-500 mb-4">未找到该剧集信息</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedShows = tvShows
    .filter((s) => s.id !== show.id && s.categories.some((c) => show.categories.includes(c)))
    .slice(0, 4);

  const mainCategory = show.categories[0];
  const categoryLabel = categoryNameMap[mainCategory] || mainCategory;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${show.name} - ${show.englishName}`,
    description: show.description,
    image: show.poster,
    datePublished: `${show.year}-01-01`,
    author: {
      '@type': 'Organization',
      name: '91美剧网',
    },
    publisher: {
      '@type': 'Organization',
      name: '91美剧网',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/all' },
                { label: categoryLabel, href: `/category/${mainCategory}` },
                { label: show.name },
              ]}
            />
          </div>

          <article className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-14">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-[300px] flex-shrink-0">
                <div className="rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={show.poster}
                    alt={`${show.name} 海报`}
                    title={`${show.name} - ${show.englishName}`}
                    className="w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {show.name}
                </h1>
                <p className="text-sm text-foreground-400 mt-1">{show.englishName}</p>

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                    {categoryLabel}
                  </span>
                  {show.categories.slice(1).map((cat) => (
                    <Link
                      key={cat}
                      to={`/category/${cat}`}
                      className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap"
                    >
                      {categoryNameMap[cat] || cat}
                    </Link>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-base" />
                    <strong className="text-foreground-950 text-lg">{show.rating}</strong>
                  </span>
                  <span>{show.year}</span>
                  <span>{show.seasons}季 {show.episodes}集</span>
                  <span className="text-primary-500 font-medium">{show.status}</span>
                </div>

                <div className="mt-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{show.description}</p>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-foreground-400">导演</span>
                    <p className="text-sm text-foreground-700 mt-0.5">{show.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">主演</span>
                    <p className="text-sm text-foreground-700 mt-0.5">{show.cast.join(' / ')}</p>
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-3 text-sm">
                  <span className="text-xs text-foreground-400">最新更新：</span>
                  <span className="text-primary-500 font-medium">{show.latestEpisode}</span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-400">{show.updateTime}</span>
                </div>
              </div>
            </div>

            <section className="mt-12">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-6">分集列表</h2>

              <div className="flex flex-wrap items-center gap-2 mb-6">
                {show.episodeList.map((season, idx) => (
                  <button
                    key={season.season}
                    onClick={() => setActiveSeason(idx)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSeason === idx
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    第{season.season}季
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {show.episodeList[activeSeason]?.episodes.map((ep) => (
                  <div
                    key={ep.num}
                    className="p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
                  >
                    <span className="text-xs text-foreground-400">第{ep.num}集</span>
                    <p className="text-sm text-foreground-700 mt-1 truncate">{ep.title}</p>
                  </div>
                ))}
              </div>
            </section>

            {relatedShows.length > 0 && (
              <section className="mt-14">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                  {relatedShows.map((s) => (
                    <ShowCard key={s.id} show={s} />
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}